import {
  Autocomplete,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import useReportingAndAnalyticsApi from "../../../useReportingAndAnalyticsApi";
import LoadingDialogComponent from "../../../../../../shared/components/loading-dialog/loading-dialog.component";
import useLoader from "../../../../../../shared/components/loading-dialog/useLoader.hook";
import IDataSourceProps from "../../../../../../shared/contracts/IDataSourceProps";
import { CreateReportFormContext } from "../../../../../../shared/context/create-report/create-report-context";

interface IProps {}

export default function DatasourceListComponent({}: IProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly IDataSourceProps[]>([]);
  const loading = options.length === 0; //open && options.length === 0; -- removing condition to issue call as soon as user lands on the screen
  const { createReportForm, dispatch } = useContext(CreateReportFormContext)!;

  const [value, setValue] = useState<IDataSourceProps | null>(
    createReportForm.selectedDataSource
  );
  const { getDatasourceList } = useReportingAndAnalyticsApi();

  const datasourcesLoader = useLoader({
    loaderName: "Load Data Sources",
    loadAsync: () =>
      getDatasourceList({
        doIf: () => datasourcesLoader.isNotLoading,
        doBefore: () => {
          datasourcesLoader.SET_LOADING("Loading Data Sources...");
        },
        onFulfilled: (data) => {
          //handleApply();
          datasourcesLoader.SET_SUCCESS("Success.!");
          setOptions([...data]);
        },
        onException: datasourcesLoader.SET_EXCEPTION,
      }),
  });

  useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      if (options.length === 0) {
        datasourcesLoader.loadAsync();
      }
    })();

    return () => {
      active = false;
    };
  }, [loading]);

  return (
    <>
      {/* Loader Dialog */}
      {<LoadingDialogComponent loaderHook={datasourcesLoader} />}

      <Autocomplete
        size="small"
        id="datasource-list-id"
        sx={{ minWidth: "30%" }}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        value={value}
        onChange={(e, newValue: IDataSourceProps | null) => {
          setValue(newValue);

          dispatch({
            type: "UPDATE",
            payload: {
              selectedDataSource: newValue,
              currentAvailableColumns: [],
            },
          });
          dispatch({
            type: "UPDATE_REPORT_SPECIFICATION",
            payload: { filterCriterias: [], columnSelections: [] },
          });
          if (newValue) {
            dispatch({
              type: "UPDATE_ERROR",
              payload: { field: "datasourceError", value: "" },
            });
          }
        }}
        isOptionEqualToValue={(option, value) =>
          option.dataSourceName === value.dataSourceName
        }
        getOptionLabel={(option) => option.dataSourceName}
        options={options}
        loading={loading}
        renderTags={(value: readonly IDataSourceProps[], getTagProps) =>
          value.map((option: IDataSourceProps, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                variant="outlined"
                label={option.dataSourceName}
                key={key}
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Data Sources"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {/* Display error message if set */}
      {createReportForm.errors.datasourceError && (
        <Typography color="error" sx={{ mt: 1 }}>
          {createReportForm.errors.datasourceError}
        </Typography>
      )}
    </>
  );
}
